import { useState } from "react";
import { useTranslation } from "react-i18next";

import { refusalText } from "../../lib/refusal.js";
import { connectDisplay } from "../../services/partyService.js";

/**
 * "Connect a TV" — the phone half of phase 11c's inverted pairing.
 *
 * # Why the code goes this way round
 *
 * A browser display is opened by URL and that is still the easy path — the box
 * above hands out `/display/CODE` for exactly that. A **packaged** television
 * has no URL bar, and ADR 0005 refused an on-screen keyboard on purpose, so the
 * code travels the other way: the television shows six characters and they are
 * typed here, on the device that already has a keyboard and is already the host.
 *
 * # It is host-only, and the server agrees
 *
 * `POST /api/party/display` refuses anybody else, so this control is rendered
 * inside the host's section rather than being offered to a guest whose tap would
 * always fail.
 *
 * # What it says when it works
 *
 * Nothing about the television. It appears in the roster on its own within a
 * second or two, which is a better confirmation than a sentence claiming it did
 * — and the roster is the thing a host is already looking at.
 */
export function ConnectTv({ busy = false }: { busy?: boolean }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The alphabet has no digits, so anything that is not a letter is a typo — and
  // stripping rather than refusing is what makes a code read off a screen in two
  // groups paste correctly.
  const typed = code.replace(/[^A-Za-z]/g, "").toUpperCase();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    setSaid(null);
    void connectDisplay(typed)
      .then(() => {
        // `answer.message` is the server's English, and `connected` is
        // documented as always `true` — a failure arrives as a status. So there
        // is exactly one thing to say, and it is said here, in this language.
        setSaid(t("connectTv.joining"));
        setCode("");
      })
      .catch((cause: unknown) => {
        setError(refusalText(cause, t));
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <form onSubmit={submit} className="mt-3 flex flex-col gap-2">
      <label htmlFor="pairing-code" className="text-xs text-white/50">
        {t("connectTv.label")}
      </label>
      <div className="flex gap-2">
        <input
          id="pairing-code"
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          autoCapitalize="characters"
          autoComplete="off"
          maxLength={8}
          placeholder="ABC DEF"
          className="min-h-12 flex-1 rounded-lg border border-white/15 bg-stage-950 px-3 font-mono uppercase tracking-widest"
        />
        <button
          type="submit"
          disabled={busy || sending || typed.length !== 6}
          className="min-h-12 rounded-lg border border-white/25 px-4 font-medium disabled:opacity-40"
        >
          {sending ? t("connectTv.connecting") : t("connectTv.action")}
        </button>
      </div>
      {said && <p className="text-xs text-white/50">{said}</p>}
      {error && (
        <p role="alert" className="text-xs text-wrong-500">
          {error}
        </p>
      )}
    </form>
  );
}
